import { useLayoutEffect } from "react";
import { Route, Routes } from "react-router";
import { Navigate, useSearchParams } from "react-router-dom";

import { IDP_CALLBACK_PATH } from "./idp-auth";
import Callback from "./pages/auth0/callback";
import LoggedOut from "./pages/logged-out/LoggedOut";
import ForgotUsername from "./pages/login/forgot-username";
import Identifier from "./pages/login/identifier";
import Password from "./pages/login/password";
import { Logout } from "./pages/logout/Logout";
import { ConfirmUnifiedAccountLink } from "./pages/unified_account_linking/ConfirmUnifiedAccountLink";
import { PromptUnifiedAccountLink } from "./pages/unified_account_linking/PromptUnifiedAccountLink";
import { UnifiedAccountSwitcher } from "./pages/unified_account_switcher/UnifiedAccountSwitcher";
import UnifiedLoginIdentifier from "./pages/unified_login/identifier";
import Start from "./pages/unified_login/start";
import { SendGridSessionContext, useSendGridSession } from "./sendgrid-auth";
import { useAccountQueries, useUnifiedAuth } from "./unified-auth";

const botDetectionEnabled = import.meta.env.VITE_BOT_DETECTION_ENABLED === "true";
const botDetectionIdentifierSiteKey =
  import.meta.env.VITE_BOT_DETECTION_IDENTIFIER_SITE_KEY || "must-override";
const botDetectionPasswordSiteKey =
  import.meta.env.VITE_BOT_DETECTION_PASSWORD_SITE_KEY || "must-override";
const botDetectionTimeoutMs = parseInt(
  import.meta.env.VITE_BOT_DETECTION_TIMEOUT_MS || "10000",
  10
);

export const LoginRoutes = () => {
  const { navigateToGuide } = useAccountQueries();
  const [searchParams] = useSearchParams();

  // If mako token is already set just navigate to the guide because it means you've already logged in.
  // However, if there are error parameters in the URL, we should stay on the login page to display the error.
  // This requires a useEffect because it causes an infinite recursion if it is not in one.
  // Also, we do not want it to fire if we change the makoToken, once we move all security pages into
  // login we can wait to set the mako token to the end of on login flow and then have this automatically redirect
  // us to the guide by watching for the mako token update.
  //
  useLayoutEffect(() => {
    (async () => {
      // Don't auto-redirect if there's an error parameter in the URL
      if (!searchParams.has("error") && !searchParams.has("error_description")) {
        await navigateToGuide();
      }
    })();
    //eslint-disable-next-line
  }, []);

  return (
    <Routes>
      <Route index element={<Navigate to="/login/identifier" replace />} />
      <Route path="/login" element={<Navigate to="/login/identifier" replace />} />
      <Route
        path="/login/password"
        element={
          <Password
            botDetectionEnabled={botDetectionEnabled}
            botDetectionSitekey={botDetectionPasswordSiteKey}
            botDetectionTimeoutMs={botDetectionTimeoutMs}
          />
        }
      />
      <Route
        path="/login/identifier"
        element={
          <Identifier
            botDetectionEnabled={botDetectionEnabled}
            botDetectionSitekey={botDetectionIdentifierSiteKey}
            botDetectionTimeoutMs={botDetectionTimeoutMs}
          />
        }
      />
      <Route path="/login/forgot-username" element={<ForgotUsername />} />
    </Routes>
  );
};

const UnifiedAccountLinkingRoutes = () => {
  const { goToLoginIfNotAuthenticatedWithSendgrid } = useUnifiedAuth();
  const { isAuthenticated: isAuthenticatedWithSendGrid } =
    useSendGridSession(SendGridSessionContext);

  useLayoutEffect(() => {
    goToLoginIfNotAuthenticatedWithSendgrid();
    //eslint-disable-next-line
  }, []);

  if (!isAuthenticatedWithSendGrid) {
    return <></>;
  }

  return (
    <Routes>
      <Route path="/prompt" element={<PromptUnifiedAccountLink />} />
      <Route path="/confirm" element={<ConfirmUnifiedAccountLink />} />
    </Routes>
  );
};

const UnifiedAccountSwitchingRoutes = () => {
  return (
    <Routes>
      <Route path="/accounts" element={<UnifiedAccountSwitcher />} />
    </Routes>
  );
};

export interface AppRoutesProps {
  enabledUnifiedAccountLinking: boolean;
  enabledUnifiedAccountSwitcher: boolean;
  enabledResellerUnifiedAccountLinking: boolean;
}

const AppRoutes = (opts: AppRoutesProps) => {
  const {
    enabledUnifiedAccountLinking,
    enabledUnifiedAccountSwitcher,
    enabledResellerUnifiedAccountLinking,
  } = opts;
  return (
    <>
      <Routes>
        <Route path={IDP_CALLBACK_PATH} element={<Callback />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        <Route path="/unified_login/start" element={<Start />} />
        <Route path="*" element={<LoginRoutes />} />
        {enabledUnifiedAccountLinking && (
          <Route path="/unified_account_linking/*" element={<UnifiedAccountLinkingRoutes />} />
        )}
        {enabledUnifiedAccountSwitcher && (
          <Route path="/unified_account_switching/*" element={<UnifiedAccountSwitchingRoutes />} />
        )}
        {/* Outside LoginRoutes intentionally: this is a session-agnostic invite
            acceptance page that should render regardless of authentication state. */}
        {enabledResellerUnifiedAccountLinking && (
          <Route path="/unified_login/identifier" element={<UnifiedLoginIdentifier />} />
        )}
      </Routes>
    </>
  );
};

export default AppRoutes;
